import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Check, Copy, Eye, EyeOff, TriangleAlert } from 'lucide-react';
import { foundation } from '../../../ui';
import * as p from '../../../ui/primitives';
import { copyToClipboard } from '../../../utils/ui/copyToClipboard';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import './mfaSetupKey.css';

const f = foundation;
const fc = f.color;

/** How long "Copied" holds before easing back (plain timer, never animationend). */
export const KEY_COPIED_MS = 1600;
const MASK_LINE = '•••• •••• •••• ••••';
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

/**
 * The raw secret as EXACTLY two display lines of 4-char groups — the first
 * half of the groups (rounded up) on line 1, the rest on line 2. A 32-char
 * base32 secret is 4 + 4 groups. Display only; copy always uses the raw value.
 */
export function secretLines(secret) {
  const raw = String(secret || '').replace(/\s+/g, '');
  const groups = raw.match(/.{1,4}/g) || [];
  const cut = Math.ceil(groups.length / 2);
  return [groups.slice(0, cut).join(' '), groups.slice(cut).join(' ')];
}

const keyBoxStyle = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${fc.neutral.border}`,
  borderRadius: f.radii.md,
  backgroundColor: fc.neutral.surface,
  padding: `${f.spacePx[3]} ${f.spacePx[3]}`,
  textAlign: 'center',
  fontFamily: f.fontFamily.mono,
  fontSize: f.fontSize.md,
  letterSpacing: '0.12em',
  color: fc.neutral.text,
};
// Fixed line box so masked ↔ revealed never changes the height. Wraps only as
// a last resort for an unusually long secret (never cut, never a sideways scroll).
const lineStyle = { display: 'block', lineHeight: '1.6em', minHeight: '1.6em', whiteSpace: 'normal', overflowWrap: 'anywhere' };
// Standard visually-hidden pattern (matches SettingsSaveBar/OfficeInboxModal)
// — the REAL accessible name for the key box. A bare `aria-label` on a
// role-less `<code>` element isn't reliably exposed to AT; a visually-hidden
// text node always is.
const srOnlyStyle = {
  position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px',
  overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
};
const actionsRow = { display: 'flex', gap: f.spacePx[2], marginTop: f.spacePx[2] };
// Two stacked children share one grid cell so a crossfade never shifts width.
const stackStyle = { display: 'inline-grid', alignItems: 'center', justifyItems: 'center' };
const layerStyle = (on, reduced) => ({
  gridArea: '1 / 1',
  display: 'inline-flex',
  opacity: on ? 1 : 0,
  transition: reduced ? 'none' : `opacity 160ms ${EASE}`,
});

/** @param {null|'success'|'danger'} beat */
function buttonStyle(compact, beat, reduced) {
  // Same done beat as the recovery-codes save rows: success-soft fill + green
  // for a successful copy; a copy FAILURE (2026-09-25) mirrors it in the
  // danger palette instead. Always set the three longhands in EVERY state.
  // p.buttonSecondary uses the `border` SHORTHAND; if a longhand were only
  // present during a beat, React would clear it when the beat ends and the
  // border would fall back to currentColor (a dark outline). Same for bg/color.
  const tint = beat === 'success'
    ? { borderColor: fc.semantic.successBorder, backgroundColor: fc.semantic.successSoft, color: fc.semantic.success }
    : beat === 'danger'
      ? { borderColor: fc.semantic.dangerBorder, backgroundColor: fc.semantic.dangerSoft, color: fc.semantic.danger }
      : {
        borderColor: p.buttonSecondary.borderColor || fc.neutral.borderStrong,
        backgroundColor: p.buttonSecondary.backgroundColor || p.buttonSecondary.background || fc.neutral.surface,
        color: p.buttonSecondary.color,
      };
  return {
    ...p.buttonSecondary,
    flex: '1 1 0',
    minWidth: 0,
    minHeight: compact ? 44 : 36,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: f.spacePx[2],
    boxSizing: 'border-box',
    ...tint,
    transition: reduced
      ? 'none'
      : `background-color 200ms ease, color 200ms ease, border-color 200ms ease, transform 120ms ${EASE}`,
  };
}

/** Icon + label that crossfade between two states in place. */
function Swap({ on, a, b, reduced }) {
  return (
    <>
      <span style={stackStyle} aria-hidden>
        <span style={layerStyle(!on, reduced)}>{a.icon}</span>
        <span style={layerStyle(on, reduced)}>{b.icon}</span>
      </span>
      <span style={stackStyle}>
        <span style={layerStyle(!on, reduced)} aria-hidden={on || undefined}>{a.label}</span>
        <span style={layerStyle(on, reduced)} aria-hidden={!on || undefined}>{b.label}</span>
      </span>
    </>
  );
}

/**
 * RD-SEC-015 setup key (2026-09-25 founder revision). The box is ALWAYS two
 * lines of four groups — masked (`•••• •••• •••• ••••` ×2) or revealed — so its
 * height never changes. Show/Copy sit UNDER it as two equal buttons. Beats:
 * reveal = characters fade in from a slight blur (180ms); Show↔Hide and
 * Copy→Copied crossfade icon + label; Copied tints success for 1.6s (plain
 * timer) then eases back; press = 0.97 scale (`cvx-auth-press`). Reduced
 * motion: instant swaps, no blur, no scale.
 */
export default function MfaSetupKeyBox({ secret, compact }) {
  const intl = useIntl();
  const reduced = useReducedMotion();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [revealGen, setRevealGen] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const lines = revealed ? secretLines(secret) : [MASK_LINE, MASK_LINE];

  const toggle = () => {
    setRevealed((v) => !v);
    setRevealGen((g) => g + 1);
  };

  const handleCopy = () => {
    void copyToClipboard(secret || '').then((ok) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (ok) {
        setCopyFailed(false);
        setCopied(true);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setCopied(false);
        }, KEY_COPIED_MS);
      } else {
        // Clipboard rejected the write (permission denied, not focused, a
        // non-secure-context quirk `copyToClipboard`'s own fallback also
        // failed) — the SAME transient beat mechanism as a success, mirrored
        // into the danger palette instead of silently doing nothing.
        setCopied(false);
        setCopyFailed(true);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setCopyFailed(false);
        }, KEY_COPIED_MS);
      }
    });
  };

  const t = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage });

  // Groups are space-separated already, which reads them out group-by-group.
  const revealedAnnounce = lines.join(' ');

  return (
    <div data-testid="mfa-key-box" data-revealed={revealed ? 'true' : 'false'}>
      <code style={keyBoxStyle} data-testid="mfa-key-code">
        {/* Visible glyphs are decorative to AT — the real accessible name is
            the visually-hidden span below (a bare `aria-label` on a role-less
            `<code>` isn't reliably exposed). Re-keyed per toggle so the
            blur-in replays on each reveal. */}
        <span key={revealGen} aria-hidden="true" data-testid="mfa-key-visible-layer" className={revealed && !reduced && revealGen > 0 ? 'cvx-mfa-key-in' : undefined} style={{ display: 'block' }}>
          {lines.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} style={lineStyle} data-testid="mfa-key-line">{line}</span>
          ))}
        </span>
        <span style={srOnlyStyle} data-testid="mfa-key-sr-label">
          {revealed ? revealedAnnounce : t('mfaStepper.keyHidden', 'Key hidden')}
        </span>
      </code>
      <div style={actionsRow}>
        <button type="button" className="cvx-auth-press" style={buttonStyle(compact, null, reduced)} onClick={toggle} aria-pressed={revealed} data-testid="mfa-key-toggle">
          <Swap
            on={revealed}
            reduced={reduced}
            a={{ icon: <Eye size={16} strokeWidth={2} />, label: t('mfaStepper.show', 'Show') }}
            b={{ icon: <EyeOff size={16} strokeWidth={2} />, label: t('mfaStepper.hide', 'Hide') }}
          />
        </button>
        <button
          type="button"
          className="cvx-auth-press"
          style={buttonStyle(compact, copyFailed ? 'danger' : copied ? 'success' : null, reduced)}
          onClick={handleCopy}
          data-testid="mfa-key-copy"
          data-copied={copied ? 'true' : 'false'}
          data-copy-failed={copyFailed ? 'true' : 'false'}
        >
          <Swap
            on={copied || copyFailed}
            reduced={reduced}
            a={{ icon: <Copy size={16} strokeWidth={2} />, label: t('mfaStepper.copy', 'Copy') }}
            b={copyFailed
              ? { icon: <TriangleAlert size={16} strokeWidth={2.2} />, label: t('mfaStepper.copyFailed', 'Couldn’t copy') }
              : { icon: <Check size={16} strokeWidth={2.6} />, label: t('mfaStepper.copied', 'Copied') }}
          />
        </button>
      </div>
    </div>
  );
}
